import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Link from "components/link";
import IconButton from "components/icon-button";
import { FormattedMessage } from "react-intl";
import Icon from "components/icon";
import { useMutation } from "@apollo/react-hooks";
import { Restricted, permissions } from "components/auth";
import { DeleteActionWithModal } from "components/buttons";
import { ConnectedPaginatedTable, TableIconCell, CustomTableRow } from "components/table";
import query from "./query";
import styles from "./styles/table.css.js";
import mutation from "./mutation";
import withStyles from "components/with-styles";
import { useAuthDataContext } from "components/auth";
import { injectIntl } from "react-intl";
import { HeaderCell } from "components/table";
import GraphicSetPreview from "./graphicset-preview";
import { getInUseGraphicSet } from "../../../services/commonService";

const Header = ({ onChangeOrdering, orderBy }) => (
  <TableRow>
    <HeaderCell />
    <HeaderCell onChangeOrdering={onChangeOrdering} orderBy={orderBy} orderName="orderByName">
      <FormattedMessage id="common.name" />
    </HeaderCell>
    <TableIconCell />
  </TableRow>
);

Header.propTypes = {
  /**
   ** Callback que se invoca al cambiar la columna actual
   **/
  onChangeOrdering: PropTypes.func,
  /**
   ** Ordenación de la columna actual
   **/
  orderBy: PropTypes.object,
};

const RowComponent = ({ classes, intl, item, refetchData }) => {
  const [deleteGraphicSet] = useMutation(mutation);
  const detailsUrl = `/graphicset/details/${item.id}/customerId/${item.customerId}`;
  const [inUseGraphicSet, setInUseGraphicSet] = useState(null);

  const handleSetInUseGraphicSet = data => {
    setInUseGraphicSet(data);
  };

  const handleGetInUseGraphicSet = useCallback(() => {
    getInUseGraphicSet(
      {
        customer_id: item.customerId,
        graphicset_id: item.id,
      },
      handleSetInUseGraphicSet,
    );
  }, []);

  useEffect(
    () => {
      if (item) {
        handleGetInUseGraphicSet();
      }
    },
    [item],
  );

  return (
    <CustomTableRow key={item.id} href={detailsUrl}>
      <TableCell>
        <GraphicSetPreview graphicSet={item} />
      </TableCell>
      <TableCell>
        <div className={classes.title}>{item.name}</div>
        <div>{item.description}</div>
      </TableCell>
      <TableIconCell colSpan={2}>
        <Link href={detailsUrl} target="_blank" rel="noopener">
          <IconButton>
            <Icon name="launch" />
          </IconButton>
        </Link>
        <Restricted permissions={[permissions.graphicset_manage]}>
          <Link href={`/graphicset/new/${item.id}/customerId/${item.customerId}`}>
            <IconButton className={classes.iconButton}>
              <Icon name="file-copy" />
            </IconButton>
          </Link>
          <Link href={`/graphicset/edit/${item.id}/customerId/${item.customerId}`}>
            <IconButton className={classes.iconButton}>
              <Icon name="edit" />
            </IconButton>
          </Link>
          {inUseGraphicSet && (
            <DeleteActionWithModal
              disabledTooltipText={intl.formatMessage({ id: "app.graphicset.delete.hasEvents" })}
              disabled={inUseGraphicSet?.is_in_use}
              mutation={deleteGraphicSet}
              mutationVariables={{
                graphicSetId: item.id,
                customerId: item.customerId,
              }}
              elementNameInResult="deleteGraphicSet"
              elementToDelete={item.name}
              refetchDataOnSuccess
              refetchData={refetchData}
              disableIcon={inUseGraphicSet?.is_in_use}
            />
          )}
        </Restricted>
      </TableIconCell>
    </CustomTableRow>
  );
};

RowComponent.propTypes = {
  /**
   ** Elemento a representar en la fila de la tabla
   **/
  item: PropTypes.object,
  /**
   * Función de refetch de datos
   */
  refetchData: PropTypes.func.isRequired,
};

const I18nStyledRowComponent = withStyles(styles)(injectIntl(RowComponent));

const GraphicSetPaginatedTable = props => {
  const { hasAdminPrivileges } = useAuthDataContext();
  return (
    <ConnectedPaginatedTable
      gqlQuery={query}
      manuallyScopedQuery={hasAdminPrivileges}
      headerComponent={Header}
      rowComponent={I18nStyledRowComponent}
      paginatedElementsHandler={data => data.graphicsets}
      defaultOrdering={{ orderByName: "ASC" }}
      {...props}
    />
  );
};

export default GraphicSetPaginatedTable;
